import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "@phosphor-icons/react";
import { useTourOpen, endTour } from "../lib/tour";
import { tourSteps } from "../lib/tourSteps";
import { useAuth } from "../context/AuthContext";
import { completeOnboarding } from "../lib/api";

const PAD = 8;
const CARD_W = 344;
const glide = { type: "spring", stiffness: 210, damping: 30, mass: 0.9 };
const visible = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
const findTarget = (sel) => [].concat(sel).map((s) => [...document.querySelectorAll(s)].find(visible)).find(Boolean) || null;
const isPhone = () => window.innerWidth < 640;

function useTarget(step, path) {
  const [el, setEl] = useState(null);
  const [rect, setRect] = useState(null);
  useEffect(() => {
    setEl(null);
    if (!step || (step.route && step.route !== path)) return undefined;
    let n = 0;
    const id = setInterval(() => {
      const found = findTarget(step.target);
      if (found || ++n > 40) {
        clearInterval(id);
        if (!found) return setRect(null);
        const r = found.getBoundingClientRect();
        const top = isPhone() || r.height > window.innerHeight * 0.55 ? r.top + window.scrollY - 88 : r.top + window.scrollY - (window.innerHeight - r.height) / 2;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        setEl(found);
      }
    }, 80);
    return () => clearInterval(id);
  }, [step, path]);
  useLayoutEffect(() => {
    if (!el) return undefined;
    let raf;
    const measure = () => { const r = el.getBoundingClientRect(); setRect((p) => (p && p.top === r.top && p.left === r.left && p.width === r.width && p.height === r.height ? p : { top: r.top, left: r.left, width: r.width, height: r.height })); };
    const loop = () => { measure(); raf = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [el]);
  return rect;
}

function placeCard(rect, cardH) {
  const vw = window.innerWidth, vh = window.innerHeight;
  if (isPhone()) return { left: 12, top: Math.max(12, vh - cardH - 12 - (window.visualViewport ? vh - window.visualViewport.height : 0)), y: 0 };
  if (!rect) return { left: vw / 2 - CARD_W / 2, top: vh / 2 - cardH / 2, y: 0 };
  const left = Math.min(Math.max(16, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 16);
  const below = rect.top + rect.height + PAD + 14;
  if (vh - below > cardH + 16) return { left, top: below, y: 0 };
  if (rect.top - PAD - 14 > cardH + 16) return { left, top: rect.top - PAD - 14 - cardH, y: 0 };
  return { left: vw - CARD_W - 24, top: vh - cardH - 24, y: 0 };
}

export default function TourHost() {
  const open = useTourOpen();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const steps = useMemo(() => tourSteps(user?.role === "admin"), [user?.role]);
  const [i, setI] = useState(0);
  const [cardH, setCardH] = useState(220);
  const cardRef = useCallback((n) => {
    if (!n) return;
    const ro = new ResizeObserver(() => setCardH(n.offsetHeight));
    ro.observe(n);
  }, []);
  const step = open ? steps[i] : null;
  const rect = useTarget(step, pathname);

  useEffect(() => { if (open) setI(0); }, [open]);
  useEffect(() => { if (step?.route && step.route !== pathname) navigate(step.route); }, [step, pathname, navigate]);

  const finish = useCallback(async () => {
    endTour();
    if (user && !user.onboarded_at) {
      const r = await completeOnboarding().catch(() => null);
      if (r) setUser((u) => ({ ...u, onboarded_at: r.onboarded_at }));
    }
  }, [user, setUser]);
  const next = useCallback(() => (i >= steps.length - 1 ? finish() : setI(i + 1)), [i, steps.length, finish]);
  const back = useCallback(() => setI((k) => Math.max(0, k - 1)), []);

  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => {
      const map = { ArrowRight: next, Enter: next, ArrowLeft: back, Escape: finish };
      if (!map[e.key] || (e.key === "Enter" && document.activeElement?.closest?.('[data-testid="tour-card"] button'))) return;
      e.preventDefault(); e.stopPropagation(); map[e.key]();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [open, next, back, finish]);

  if (!open || !step) return null;
  const pos = placeCard(rect, cardH);
  const vw = window.innerWidth;
  const hole = rect ? (() => { const left = Math.max(4, rect.left - PAD), right = Math.min(vw - 4, rect.left + rect.width + PAD); return { top: rect.top - PAD, left, width: right - left, height: rect.height + PAD * 2, opacity: 1 }; })()
    : { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0, opacity: 1 };
  const last = i === steps.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-[90]" data-testid="tour-overlay" role="dialog" aria-modal="true" aria-label="Site tour">
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />
      <motion.div aria-hidden className="fixed rounded-[20px] pointer-events-none" data-testid="tour-spotlight"
        initial={{ ...hole, opacity: 0 }} animate={hole} transition={glide}
        style={{ boxShadow: "0 0 0 9999px rgba(10,12,10,0.58), 0 0 0 1.5px rgba(255,255,255,0.55), 0 0 32px 4px rgba(255,255,255,0.18)" }} />
      <motion.div ref={cardRef}
        className="fixed rounded-[26px] bg-background/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-black/5 dark:ring-white/10 overflow-hidden"
        style={{ width: isPhone() ? "calc(100vw - 24px)" : CARD_W }} initial={{ ...pos, opacity: 0, scale: 0.96 }} animate={{ ...pos, opacity: 1, scale: 1 }} transition={glide} data-testid="tour-card">
        <div className="h-1 bg-muted"><motion.div className="h-full" style={{ background: step.color }} animate={{ width: `${((i + 1) / steps.length) * 100}%` }} transition={glide} /></div>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground tabular-nums" data-testid="tour-step-count">{i + 1} of {steps.length}</span>
            <button type="button" onClick={finish} aria-label="End tour" data-testid="tour-close" className="h-7 w-7 -mr-1 rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"><X className="h-3.5 w-3.5" /></button>
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18, ease: "easeOut" }}>
              <div className="mt-3 flex items-center gap-3">
                <span className="h-10 w-10 rounded-2xl inline-flex items-center justify-center shrink-0" style={{ background: `${step.color}1f`, color: step.color }}><step.Icon weight="fill" className="h-5 w-5" /></span>
                <h2 className="text-lg font-semibold tracking-tight leading-tight" data-testid="tour-title">{step.title}</h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid="tour-text">{step.text}</p>
            </motion.div>
          </AnimatePresence>
          <div className="mt-5 flex items-center gap-2">
            {!last && <button type="button" onClick={finish} data-testid="tour-skip" className="h-10 px-3 rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors">Skip</button>}
            <span className="flex-1" />
            {i > 0 && <button type="button" onClick={back} aria-label="Previous step" data-testid="tour-back" className="h-10 w-10 rounded-full bg-muted/70 inline-flex items-center justify-center hover:bg-muted active:scale-95 transition-[background-color,transform]"><ArrowLeft className="h-4 w-4" /></button>}
            <button type="button" onClick={next} data-testid="tour-next" className="h-10 pl-5 pr-4 rounded-full text-sm font-semibold bg-foreground text-background inline-flex items-center gap-1.5 active:scale-95 transition-transform">
              {last ? "Done" : "Next"}{!last && <ArrowRight weight="bold" className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
